import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { translate } from 'services/i18nService';
import { REJOIN_SPY_SHOW_ERROR } from 'services/MessagingService/EventNames';
import createMessagingService from 'services/MessagingService/MessagingService';
import ModalService from 'services/ModalService';
import FanClubStore from 'stores/FanClubStore';
import FirebaseStore from 'stores/FirebaseStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import TokenUpchargeDisclaimer from 'components/TokenUpchargeDisclaimer';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';

import spyGraphic from '../../../images/mobileProfile/spy-graphic.png';
import privateRoomLockedImg from '../../../images/privateRoomLocked.png';

import styles from './DesktopBroadcastEnterSpy.css';


const DesktopBroadcastEnterSpy = (props) => {
  const {
    accessLevel,
    handlePrivateShowAction,
    isSfw,
    showBuyTokensModal,
  } = props;

  const { performerFanClubInfo: { viewerSubscribed } } = FanClubStore;
  const { allowSpyShow, spyShowPricePerMinute } = FirebaseStore;
  const { balance } = PaymentStore;
  const {
    performerDetails: { username: performerName },
    isPlayerBroadcastMode,
  } = ProfileStore;

  const messagingService = createMessagingService();

  const [startSpyLoading, setStartSpyLoading] = useState(false);

  useEffect(() => {
    messagingService.on(REJOIN_SPY_SHOW_ERROR, () => {
      setStartSpyLoading(false);
    });

    return () => {
      messagingService.off(REJOIN_SPY_SHOW_ERROR, () => {
        setStartSpyLoading(false);
      });
    };
  }, [messagingService]);

  const isAdmin = accessLevel > ACCESS_LEVEL_NUM.USER_MODERATOR;
  const hasEnoughTokens = parseInt(spyShowPricePerMinute, 10) <= balance;
  const hasEnoughTokensOrCanSpyForFree = (
    hasEnoughTokens ||
    isAdmin ||
    viewerSubscribed
  );

  const onClickStartSpyShow = useCallback(() => {
    setStartSpyLoading(true);

    const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (isGuestBilling) {
      setStartSpyLoading(false);

      const modalKey = ModalService.MODAL_KEYS.GUEST_BILLING_MODAL;
      const modalProps = {
        close: ModalService.hideModal,
        show: true,
      };

      ModalService.showModal(modalKey, modalProps);

      return;
    }

    if (!hasEnoughTokensOrCanSpyForFree) {
      setStartSpyLoading(false);

      const modalKey = ModalService.MODAL_KEYS.INSUFFICIENT_BALANCE_MODAL;
      const modalProps = {
        close: ModalService.hideModal,
        show: true,
        showBuyTokensModal,
      };

      ModalService.showModal(modalKey, modalProps);

      return;
    }

    if (hasEnoughTokensOrCanSpyForFree) {
      handlePrivateShowAction('startSpy');

      if (isAdmin) {
        ProfileStore.setAdminSpyShowRequested(true, Date.now());
      }

      return;
    }

    setStartSpyLoading(false);
    showBuyTokensModal();
  }, [
    accessLevel,
    handlePrivateShowAction,
    hasEnoughTokensOrCanSpyForFree,
    isAdmin,
    showBuyTokensModal,
  ]);

  return (
    <div className={styles.spyShowEnterWrapper}>
      <div className={styles.spyShowInfoWrapper}>
        <div className={styles.spyShowName}>
          {!allowSpyShow && !isAdmin && (
            <img
              alt="locked content"
              className={styles.lockedPic}
              src={privateRoomLockedImg}
            />
          )}
          <p>
            {translate(
              'v2profile.performer.in.private.show',
              { 0: performerName, 1: '<strong>', 2: '</strong>' }
            )}
          </p>
        </div>
        <div>
          {(allowSpyShow || isAdmin) && (
            <div className={styles.spyShowDescription}>
              <img
                alt="spy this show"
                className={styles.spyGraphic}
                src={spyGraphic}
              />
              <p className={styles.title}>
                {translate('v2profile.spy.on.this.show')}
              </p>
              {viewerSubscribed ? (
                <div className={styles.spyShowText}>
                  {translate('v2profile.spyShow.fanClubInfo')}
                </div>
              ) : (
                <Fragment>
                  <div className={styles.spyShowText}>
                    {translate('v2profile.enjoy.spy.show', { 0: spyShowPricePerMinute })}
                  </div>
                  <TokenUpchargeDisclaimer className={styles.tokenUpchargeDisclaimer} />
                </Fragment>
              )}
              <LoadingButton
                colour="orange"
                customContainerClass={styles.loadingButton}
                disabled={isSfw || isPlayerBroadcastMode}
                id="startSpyButtonDesktop"
                isLoading={startSpyLoading}
                onClick={onClickStartSpyShow}
              >
                {isAdmin ? translate('v2profile.admin.spy') : translate('v2profile.spyShow.spyNowButton')}
              </LoadingButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DesktopBroadcastEnterSpy.propTypes = {
  accessLevel: PropTypes.number.isRequired,
  handlePrivateShowAction: PropTypes.func.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  isSfw: PropTypes.bool,
};

DesktopBroadcastEnterSpy.defaultProps = { isSfw: false };

export default observer(DesktopBroadcastEnterSpy);
